/**
 * There are three kinds of data in tables:
 * text, numbers, and enums
 *
 */
export enum Kind {
  LITERAL = 'LITERAL',
  NUMERICAL = 'NUMERICAL',
  DATE = 'DATE',
  ENUM = 'ENUM',
  ENUM_MULT = 'ENUM_MULT',
  NONE = 'NONE', //unfilterable
}

/**
 * There are five ways to display a data: four numerical (float/int/$/%) and one literal
 * Sixth type is for enums
 */
export enum Type {
  FLOATING = 'FLOATING',
  BYTES = 'BYTES',
  INTEGER = 'INTEGER',
  MONETARY = 'MONETARY',
  PERCENTAGE = 'PERCENTAGE',
  STRING = 'STRING',
  DATE = 'DATE',
  ID = 'ID',
  NONE = 'NONE', // unfilterable column
}

export type format = {
  prefix?: string;
  suffix?: string;
  round?: number;
  integerSeparator?: string;
};

/**
 * This is how they correspond to each other
 */
export const KINDS: Record<Type, Kind> = {
  [Type.FLOATING]: Kind.NUMERICAL,
  [Type.INTEGER]: Kind.NUMERICAL,
  [Type.MONETARY]: Kind.NUMERICAL,
  [Type.PERCENTAGE]: Kind.NUMERICAL,
  [Type.STRING]: Kind.LITERAL,
  [Type.DATE]: Kind.DATE,
  [Type.ID]: Kind.ENUM,
  [Type.BYTES]: Kind.NUMERICAL,
  [Type.NONE]: Kind.NONE,
};

export type KindForType<T extends Type> = T extends Type.FLOATING
  ? Kind.NUMERICAL
  : T extends Type.INTEGER
    ? Kind.NUMERICAL
    : T extends Type.MONETARY
      ? Kind.NUMERICAL
      : T extends Type.PERCENTAGE
        ? Kind.NUMERICAL
        : T extends Type.STRING
          ? Kind.LITERAL
          : T extends Type.DATE
            ? Kind.DATE
            : T extends Type.ID
              ? Kind.ENUM
              : T extends Type.BYTES
                ? Kind.NUMERICAL
                : T extends Type.NONE
                  ? Kind.NONE
                  : never;
export type TypeForKind<K extends Kind> = K extends Kind.DATE
  ? Type.DATE
  : K extends Kind.ENUM
    ? Type.ID
    : K extends Kind.LITERAL
      ? Type.STRING
      : K extends Kind.NONE
        ? Type.NONE
        : K extends Kind.NUMERICAL
          ? Type.BYTES | Type.FLOATING | Type.INTEGER | Type.MONETARY | Type.PERCENTAGE
          : never;

export type DatatypeForType<T extends Type> = T extends Type.FLOATING
  ? number
  : T extends Type.INTEGER
    ? number
    : T extends Type.MONETARY
      ? number
      : T extends Type.PERCENTAGE
        ? number
        : T extends Type.STRING
          ? string
          : T extends Type.DATE
            ? Date
            : T extends Type.ID
              ? number | string
              : T extends Type.BYTES
                ? number
                : T extends Type.NONE
                  ? null
                  : never;

export type Magnitude = 'B' | 'KB' | 'MB' | 'GB';
/**
 * These are instructions of how to display a certain type of numerical data
 */
export const FORMATS: { [index: string]: format } = {
  [Type.MONETARY]: {
    prefix: '$',
    round: 2,
    integerSeparator: ',',
  },
  [Type.PERCENTAGE]: {
    suffix: '%',
    round: 2,
  },
  [Type.FLOATING]: {
    round: 2,
    integerSeparator: ',',
  },
  [Type.INTEGER]: {
    round: 0,
    integerSeparator: ',',
  },
};

// Conditions have several boundaries
export enum Boundary {
  GE = 'ge',
  LE = 'le',
  FROM = 'from',
  TO = 'to',
  TEXT = 'text',
  ENUM = 'enum',
}

export type TCondition = {
  [Boundary.GE]?: number;
  [Boundary.LE]?: number;
  [Boundary.FROM]?: Date;
  [Boundary.TO]?: Date;
  [Boundary.TEXT]?: string;
  [Boundary.ENUM]?: (string | number | null)[];
};
export type ValueTunerConditionRaw = {
  [Boundary.GE]?: string;
  [Boundary.LE]?: string;

  [Boundary.TEXT]?: string;
};

export const conditionIsEmpty = (c: TCondition) => {
  if (c.enum && c.enum.length !== 0) return false;
  if (c.from !== undefined) return false;
  if (c.to !== undefined) return false;
  if (c.le !== undefined) return false;
  if (c.ge !== undefined) return false;
  if (c.text !== undefined) return false;
  return true;
};

export const BOUNDARY_TYPES = {
  [Boundary.GE]: Kind.NUMERICAL,
  [Boundary.LE]: Kind.NUMERICAL,
  [Boundary.FROM]: Kind.DATE,
  [Boundary.TO]: Kind.DATE,
  [Boundary.TEXT]: Kind.LITERAL,
  [Boundary.ENUM]: Kind.ENUM,
};
